import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import MaterialTable from "@material-table/core";
import {
  Box,
  TextField,
  InputAdornment,
  Fade,
  Tooltip,
  CircularProgress,
  useTheme,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  LiquidPaper,
  LiquidTableContainer,
  LiquidTableToolbar,
  LiquidBackground,
  LiquidSkeleton,
  LiquidEmptyState,
  LiquidIconButton,
  LiquidChip,
} from "./ModernTableStyles";
import InboxIcon from "@mui/icons-material/Inbox";

/**
 * ModernLiquidTable - A feature-rich table component with modern liquid/glassmorphism design
 *
 * Features:
 * - Client-side search with 300ms debouncing
 * - Manual pagination with customizable page sizes
 * - Fixed header and pagination bars
 * - Loading and empty states
 * - Export functionality
 * - Refresh capability
 * - Responsive design
 *
 * Performance Notes:
 * - Optimized for datasets under 500 rows
 * - For 500-1000 rows: expect slight lag during search
 * - For 1000+ rows: consider implementing server-side pagination and virtual scrolling
 *
 * @param {Object} props - Component props
 * @param {Array} props.columns - Column definitions (MaterialTable format)
 * @param {Array} props.data - Table data array
 * @param {string} props.title - Table title (optional)
 * @param {boolean} props.loading - Loading state (default: false)
 * @param {Function} props.onRefresh - Refresh callback function
 * @param {Function} props.exportFunction - Export handler function(columns, data)
 * @param {boolean} props.searchable - Enable search bar (default: true)
 * @param {boolean} props.filterable - Reserved for future use (default: true)
 * @param {ReactNode} props.customToolbarActions - Additional toolbar actions
 * @param {string} props.emptyStateMessage - Empty state message
 * @param {Object} props.options - MaterialTable options object
 * @returns {JSX.Element} ModernLiquidTable component
 */
const ModernLiquidTable = ({
  columns,
  data,
  title,
  loading = false,
  onRefresh,
  exportFunction,
  searchable = true,
  filterable = true,
  customToolbarActions,
  emptyStateMessage = "Tiada data untuk dipaparkan",
  options = {},
  ...otherProps
}) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [animatedData, setAnimatedData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(options.pageSize || 10);
  const tableRef = useRef();

  // Debounce search input for better performance (150ms for responsive feel)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 150);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Animate data on mount and updates with stagger effect
  useEffect(() => {
    if (data && data.length > 0) {
      setAnimatedData([]);
      const timer = setTimeout(() => {
        setAnimatedData(data);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setAnimatedData([]);
    }
  }, [data]);

  // Filter data based on search (debounced for performance)
  const filteredData = useMemo(() => {
    if (!debouncedSearchText || debouncedSearchText.trim() === "") return animatedData;

    const searchLower = debouncedSearchText.toLowerCase().trim();
    return animatedData.filter((row) => {
      return Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchLower);
      });
    });
  }, [animatedData, debouncedSearchText]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchText]);

  // Enhanced column configuration with liquid design
  const enhancedColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      headerStyle: {
        background: "transparent",
        color: "#ffffff",
        fontWeight: 600,
        padding: "14px 16px",
        border: "none",
        ...col.headerStyle,
      },
      cellStyle: {
        color: theme.palette.mode === "dark" ? "#e0e0e0" : "#636e72",
        padding: "14px 16px",
        ...col.cellStyle,
      },
      render:
        col.render ||
        ((rowData) => {
          const value = rowData[col.field];

          // Handle chips with liquid style
          if (col.type === "chip" || col.isChip) {
            return (
              <LiquidChip
                label={value}
                className={`status-${
                  col.getStatusClass ? col.getStatusClass(value) : "default"
                }`}
              />
            );
          }

          // Handle actions with liquid buttons
          if (col.type === "action" && col.actions) {
            return (
              <Box sx={{ display: "flex", gap: 1 }}>
                {col.actions.map((action, index) => (
                  <LiquidIconButton
                    key={index}
                    onClick={() => action.onClick(rowData)}
                    size="small"
                  >
                    {action.icon}
                  </LiquidIconButton>
                ))}
              </Box>
            );
          }

          return value;
        }),
    }));
  }, [columns, theme]);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  const handleExport = useCallback(async () => {
    if (exportFunction) {
      setIsExporting(true);
      try {
        await exportFunction(columns, filteredData);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        setIsExporting(false);
      }
    }
  }, [exportFunction, columns, filteredData]);

  const handleRefresh = useCallback(async () => {
    if (onRefresh) {
      await onRefresh();
    }
  }, [onRefresh]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Material Table options with liquid design enhancements - MUST be before conditional returns
  const liquidOptions = useMemo(
    () => ({
      ...options, // Spread options first
      search: false, // We use custom search
      toolbar: false, // We use custom toolbar
      paging: false, // We handle pagination manually
      headerStyle: {
        background: "transparent",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.95rem",
        textTransform: "uppercase",
        padding: "16px",
        border: "none",
      },
      rowStyle: (_rowData, index) => ({
        background:
          index % 2 === 0
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(255, 255, 255, 0.08)",
        backdropFilter: "blur(10px)",
        borderRadius: "12px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }),
      actionsColumnIndex:
        options.actionsColumnIndex !== undefined
          ? options.actionsColumnIndex
          : -1,
      selection: options.selection || false,
      exportButton: false, // We use custom export
      addRowPosition: options.addRowPosition || "first",
      padding: options.padding || "normal",
    }),
    [options]
  );

  // Get paginated data with row numbers - MUST be before conditional returns
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex).map((row, index) => ({
      ...row,
      _rowNumber: startIndex + index + 1, // Add global row number for display
    }));
  }, [filteredData, page, rowsPerPage]);

  // Calculate dynamic table height based on rows per page
  const tableBodyHeight = useMemo(() => {
    const rowHeight = 56; // Approximate height per row
    const minHeight = rowHeight * Math.min(paginatedData.length, rowsPerPage);
    return minHeight;
  }, [rowsPerPage, paginatedData.length]);

  // Custom toolbar render function
  const renderToolbar = () => (
    <LiquidTableToolbar>
      <Box className="toolbar-title">
        {title || `Rekod: ${filteredData.length}`}
      </Box>

      <Box className="toolbar-actions">
        {searchable && (
          <TextField
            variant="outlined"
            size="small"
            placeholder="Cari..."
            value={searchText}
            onChange={handleSearchChange}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#667eea" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgba(102, 126, 234, 0.3)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "2px solid #667eea",
                },
              },
            }}
          />
        )}

        {exportFunction && (
          <Tooltip title="Eksport">
            <LiquidIconButton onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon />
              )}
            </LiquidIconButton>
          </Tooltip>
        )}

        {onRefresh && (
          <Tooltip title="Refresh">
            <LiquidIconButton onClick={handleRefresh}>
              <RefreshIcon />
            </LiquidIconButton>
          </Tooltip>
        )}

        {customToolbarActions}
      </Box>
    </LiquidTableToolbar>
  );

  // Loading state with liquid skeleton
  if (loading && (!data || data.length === 0)) {
    return (
      <LiquidPaper>
        {renderToolbar()}
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              color: theme.palette.mode === 'dark' ? '#b0b0b0' : '#636e72',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            Memuatkan data...
          </Box>
          {[...Array(rowsPerPage)].map((_, index) => (
            <LiquidSkeleton key={index} />
          ))}
        </Box>
      </LiquidPaper>
    );
  }

  // Empty state with liquid animation
  if (!filteredData || filteredData.length === 0) {
    return (
      <LiquidPaper>
        {renderToolbar()}
        <LiquidEmptyState>
          <Box className="empty-icon">
            <InboxIcon />
          </Box>
          <Box className="empty-title">Tiada Data</Box>
          <Box className="empty-description">{emptyStateMessage}</Box>
        </LiquidEmptyState>
      </LiquidPaper>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <LiquidBackground />

      {/* Loading overlay when refreshing data */}
      {loading && data && data.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(4px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "24px",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)",
              backdropFilter: "blur(20px)",
              borderRadius: "16px",
              padding: "24px 40px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
            }}
          >
            <CircularProgress
              size={40}
              sx={{
                color: "#667eea",
              }}
            />
            <Box
              sx={{
                color: "#667eea",
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              Memuatkan data...
            </Box>
          </Box>
        </Box>
      )}

      <LiquidPaper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {renderToolbar()}

        {/* Fixed table header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px 12px 0 0",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)",
            position: "sticky",
            top: 0,
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "table",
              width: "100%",
              tableLayout: "fixed",
            }}
          >
            <Box
              sx={{
                display: "table-row",
                "& > div": {
                  display: "table-cell",
                  padding: "16px",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                  "&:last-child": {
                    borderRight: "none",
                  },
                },
              }}
            >
              {enhancedColumns.map((col, index) => (
                <Box key={index} style={{ width: col.width || "auto" }}>
                  {col.title}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Scrollable table body */}
        <Box
          sx={{
            flex: "0 1 auto",
            overflow: "hidden",
            position: "relative",
            minHeight: `${tableBodyHeight}px`,
            maxHeight: "calc(80vh - 240px)",
          }}
        >
          <LiquidTableContainer
            sx={{
              height: "100%",
              minHeight: `${tableBodyHeight}px`,
              overflow: "auto",
              position: "relative",
              "& table": {
                borderCollapse: "separate",
                borderSpacing: 0,
              },
              "& thead": {
                display: "none", // Hide the original header since we have a fixed one
              },
              "& tbody": {
                "& tr": {
                  marginTop: "8px",
                },
              },
            }}
          >
            <MaterialTable
              tableRef={tableRef}
              columns={enhancedColumns}
              data={paginatedData} // Use paginated data
              options={{
                ...liquidOptions,
                showTitle: false,
                search: false,
                toolbar: false,
              }}
              components={{
                Container: props => <div {...props} style={{ overflow: "visible" }} />,
              }}
              localization={{
                toolbar: {
                  searchTooltip: "Cari",
                  searchPlaceholder: "Cari",
                  exportTitle: "Eksport",
                  exportAriaLabel: "Eksport",
                  exportName: "Eksport sebagai CSV",
                },
                body: {
                  emptyDataSourceMessage: emptyStateMessage,
                  filterRow: {
                    filterTooltip: "Filter",
                  },
                },
              }}
              {...otherProps}
            />
          </LiquidTableContainer>
        </Box>

        {/* Fixed pagination at bottom */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.05) 100%)",
            backdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "0 0 24px 24px",
            padding: "12px 24px",
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={options.pageSizeOptions || [5, 10, 20, 50, 100]}
            labelRowsPerPage="Baris setiap halaman:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} daripada ${count}`
            }
            sx={{
              ".MuiTablePagination-root": {
                overflow: "visible",
              },
              ".MuiTablePagination-toolbar": {
                minHeight: "48px",
                padding: "0",
                display: "flex",
                alignItems: "center",
                gap: "24px",
                flexWrap: "nowrap",
              },
              ".MuiTablePagination-spacer": {
                display: "none", // Remove spacer for compact design
              },
              ".MuiTablePagination-selectLabel": {
                margin: 0,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: theme.palette.mode === "dark" ? "#b0b0b0" : "#636e72",
                flexShrink: 0,
              },
              ".MuiTablePagination-select": {
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.palette.mode === "dark" ? "#ffffff" : "#2d3436",
                background: "rgba(255, 255, 255, 0.08)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "8px",
                padding: "4px 28px 4px 10px",
                marginLeft: "8px",
                marginRight: "0",
                minWidth: "60px",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.12)",
                  borderColor: "rgba(102, 126, 234, 0.3)",
                },
                "&:focus": {
                  background: "rgba(102, 126, 234, 0.08)",
                  borderColor: "#667eea",
                  outline: "none",
                },
              },
              ".MuiTablePagination-selectIcon": {
                top: "calc(50% - 12px)",
                right: "4px",
                color: "#667eea",
              },
              ".MuiTablePagination-displayedRows": {
                fontSize: "0.875rem",
                fontWeight: 500,
                color: theme.palette.mode === "dark" ? "#b0b0b0" : "#636e72",
                marginLeft: "0",
                marginRight: "0",
                marginTop: 2,
                flexShrink: 0,
              },
              ".MuiTablePagination-actions": {
                marginLeft: "0",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: "2px",
                "& button": {
                  padding: "6px",
                  minWidth: "32px",
                  height: "32px",
                  color: "#667eea",
                  borderRadius: "8px",
                  background: "rgba(102, 126, 234, 0.05)",
                  border: "1px solid rgba(102, 126, 234, 0.1)",
                  transition: "all 0.2s ease",
                  "& .MuiSvgIcon-root": {
                    fontSize: "20px",
                  },
                  "&:hover:not(:disabled)": {
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderColor: "transparent",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  },
                  "&:disabled": {
                    opacity: 0.4,
                    color: "rgba(102, 126, 234, 0.3)",
                    background: "rgba(255, 255, 255, 0.02)",
                    borderColor: "rgba(255, 255, 255, 0.05)",
                  },
                },
              },
              // Create visual grouping with subtle backgrounds
              "& > div": {
                display: "flex",
                alignItems: "center",
                gap: "20px",
                "& > div:first-of-type": {
                  // Rows per page section
                  display: "flex",
                  alignItems: "center",
                  background: "rgba(255, 255, 255, 0.03)",
                  padding: "6px 12px",
                  borderRadius: "10px",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                },
              },
            }}
          />
        </Box>
      </LiquidPaper>

    </Box>
  );
};

export default ModernLiquidTable;
